import { useEffect, useMemo, useRef, type ReactNode, type CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import { useApp, Button, NakedButton, Banner } from '@/components/ds';
import { Code, CodeBlock, Tracker } from '@/components/code';
import { genAll, jsonStr } from '@/lib/codegen';
import { GROUPS, RES, PAYMENT_CREATE_SPEC } from '@/data';
import { ParamTable, EndpointDoc, RefNav } from '@/containers/reference';
import { ApiOverview } from '@/containers/reference-hero';
import { buildSections, buildSlugToFullId } from '@/lib/referenceSections';
import { useScrollSpy } from '@/lib/useScrollSpy';
import type { Go } from '@/lib/types';

// guides.tsx — getting-started guide pages + the ApiReference shell.

interface ProseProps {
  children?: ReactNode;
}
function Prose({ children }: ProseProps) {
  return (
    <div className="ref-body" style={{ color: 'var(--text-secondary)' }}>
      {children}
    </div>
  );
}

interface H2Props {
  children?: ReactNode;
  style?: CSSProperties;
}
function H2({ children, style }: H2Props) {
  return (
    <h2 className="ref-h2" style={{ margin: '32px 0 12px', ...style }}>
      {children}
    </h2>
  );
}

interface TwoColProps {
  children?: ReactNode;
}
function TwoCol({ children }: TwoColProps) {
  return <div className="doc-cols">{children}</div>;
}

interface GuideDocProps {
  id: string;
  go: Go;
}
function GuideDoc({ id, go }: GuideDocProps) {
  const app = useApp();
  if (id === 'intro') {
    const langs = useMemo(() => genAll(PAYMENT_CREATE_SPEC, app.apiKey), [app.apiKey]);
    return (
      <TwoCol>
        <div style={{ minWidth: 0, paddingBottom: 60 }}>
          <div className="ref-caption" style={{ color: 'var(--accent)', marginBottom: 8 }}>
            Melio API · v2
          </div>
          <h1 className="ref-h1" style={{ margin: 0 }}>
            Introduction
          </h1>
          <p className="ref-body-lg" style={{ marginTop: 14 }}>
            The Melio API lets you pay any business in the US by ACH bank transfer or paper check. You onboard an <strong>entity</strong> —
            for now, always a business — attach <strong>accounts</strong> (internal funding sources you debit, external delivery methods you
            pay to), and create a <strong>payment</strong>.
          </p>
          <H2>The integration order is the resource order</H2>
          <Prose>Every payout is built from these resources, created in sequence. Most integrations are these four calls.</Prose>
          <div style={{ marginTop: 16 }}>
            <Tracker
              vertical
              current={4}
              steps={[
                { label: 'Entity', sub: 'A payment-eligible business (KYC).' },
                { label: 'Internal account', sub: 'The funding source you debit.' },
                { label: 'External account', sub: 'Where money lands — ACH or check.' },
                { label: 'Payment', sub: 'Move the money; track the lifecycle.' },
              ]}
            />
          </div>
          <H2>Base URL & conventions</H2>
          <Prose>
            <ul style={{ margin: '8px 0', paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <li>
                All requests go to <Code>https://api.melio.com/v2</Code> over HTTPS.
              </li>
              <li>
                Amounts are <strong>integers in minor units</strong> (cents). <Code>125000</Code> = $1,250.00.
              </li>
              <li>
                Dates are <Code>YYYY-MM-DD</Code>. IDs are opaque, prefixed strings (<Code>ent_</Code>, <Code>acct_</Code>,{' '}
                <Code>pay_</Code>).
              </li>
              <li>
                The <Code>me</Code> sentinel resolves any <Code>{'{entityId}'}</Code> to your sole entity.
              </li>
            </ul>
          </Prose>
          <div style={{ marginTop: 24 }}>
            <Button onClick={() => go('/onboarding')} leftIcon="add">
              Get your API key
            </Button>
          </div>
        </div>
        <div className="doc-aside" style={{ position: 'sticky', top: 88, minWidth: 0 }}>
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              color: 'var(--text-secondary)',
              marginBottom: 8,
              textTransform: 'uppercase',
              letterSpacing: '.04em',
            }}
          >
            Create a payment
          </div>
          <CodeBlock langs={langs} dark={app.codeDark} maxHeight={320} />
          <div style={{ marginTop: 14 }}>
            <CodeBlock dark={app.codeDark} title="201 Created" code={jsonStr(RES.payment)} maxHeight={260} />
          </div>
        </div>
      </TwoCol>
    );
  }
  if (id === 'auth') {
    return (
      <TwoCol>
        <div style={{ minWidth: 0, paddingBottom: 60 }}>
          <h1 className="ref-h1" style={{ margin: 0 }}>
            Authentication
          </h1>
          <p className="ref-body-lg" style={{ marginTop: 14 }}>
            Authenticate every request with your secret API key in the <Code>api-key</Code> header. Keys come in <strong>test</strong> and{' '}
            <strong>live</strong> variants — test keys only ever touch sandbox data. Adding an account also accepts a scoped{' '}
            <Code>Bearer</Code> session token (minted by Generate an account link), which can only create internal accounts.
          </p>
          <Banner kind="warning" style={{ marginTop: 20 }}>
            Your secret key can move real money. Keep it server-side, never in browsers or mobile apps, and never commit it to source
            control.
          </Banner>
          <H2>Key types</H2>
          <ParamTable
            rows={[
              ['melio_secret_staging01_…', 'Test mode', 'Sandbox only. Safe to experiment — no money moves.'],
              ['melio_secret_production_…', 'Live mode', 'Production. Every successful payment debits a real account.'],
            ]}
          />
          <H2>Rotating keys</H2>
          <Prose>
            Rotate a key from the dashboard. Rotation issues a new secret and keeps the old one valid for a 24-hour grace window so you can
            roll deploys without downtime.
          </Prose>
          <div style={{ marginTop: 16 }}>
            <NakedButton rightIcon="arrow-right" onClick={() => go('/dashboard')}>
              Manage keys in the dashboard
            </NakedButton>
          </div>
        </div>
        <div className="doc-aside" style={{ position: 'sticky', top: 88, minWidth: 0 }}>
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              color: 'var(--text-secondary)',
              marginBottom: 8,
              textTransform: 'uppercase',
              letterSpacing: '.04em',
            }}
          >
            Authenticated request
          </div>
          <CodeBlock
            dark={app.codeDark}
            langs={{
              curl: `curl https://api.melio.com/v2/entities \\\n  -H "api-key: ${app.apiKey}"`,
              node: `import { Melio } from "@melio/sdk";\n\nconst melio = new Melio("${app.apiKey}");\nconst entities = await melio.entities.list();`,
              python: `import os\nfrom melio import Melio\n\nmelio = Melio(os.environ["MELIO_API_KEY"])\nentities = melio.entities.list()`,
              go: `client := melio.New("${app.apiKey}")\nentities, err := client.Entities.List(context.Background())`,
            }}
            maxHeight={300}
          />
          <div style={{ marginTop: 14 }}>
            <CodeBlock
              dark={app.codeDark}
              title="401 Unauthorized"
              code={jsonStr({ error: { code: 'UNAUTHORIZED', message: 'No valid api-key was provided.' } })}
              maxHeight={160}
            />
          </div>
        </div>
      </TwoCol>
    );
  }
  if (id === 'envs') {
    return (
      <TwoCol>
        <div style={{ minWidth: 0, paddingBottom: 60 }}>
          <h1 className="ref-h1" style={{ margin: 0 }}>
            Test &amp; live mode
          </h1>
          <p className="ref-body-lg" style={{ marginTop: 14 }}>
            Every account has two fully isolated environments. The key prefix determines which one you hit — there's no separate base URL to
            remember.
          </p>
          <H2>Sample data, on tap</H2>
          <Prose>
            New accounts land in test mode pre-seeded with a sample entity, a verified internal account, and an external account — so your
            very first call returns realistic data.
          </Prose>
          <H2>Deterministic simulators</H2>
          <Prose>
            Magic values force specific outcomes in test mode, so you can build and test the unhappy paths without waiting days for an ACH
            return. Use Simulate a payment action to drive a payment through risk review, collection, and delivery on demand.
          </Prose>
          <ParamTable
            rows={[
              ['amount: 5201', 'ACH return', 'Settles, then returns with code R01 (insufficient funds).'],
              ['routingNumber: 110000000', 'Instant verify', 'Internal account verifies immediately, no micro-deposits.'],
              ['taxInfo.identifier: 000000000', 'KYC fail', 'Entity stays ineligible with an incomplete-kyc reason.'],
            ]}
          />
        </div>
        <div className="doc-aside" style={{ position: 'sticky', top: 88, minWidth: 0 }}>
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              color: 'var(--text-secondary)',
              marginBottom: 8,
              textTransform: 'uppercase',
              letterSpacing: '.04em',
            }}
          >
            Force an ACH return in test
          </div>
          <CodeBlock
            dark={app.codeDark}
            langs={{
              curl: `curl https://api.melio.com/v2/entities/me/payments \\\n  -H "api-key: melio_secret_staging01_…" \\\n  -H "Idempotency-Key: 7b2e1a4c-9f3d-4e6a-8b1c-2d3e4f5a6b7c" \\\n  -X POST \\\n  -d '{\n    "amount": 5201,\n    "originatingAccountId": "acct_3f2504e0-4f89-41d3-9a0c-0305e82c3301",\n    "receivingAccountId": "acct_9b1deb4d-3b7d-4bad-9bdd-2b0d7b3dcb6d",\n    "scheduledDate": "2026-06-10",\n    "deliveryPreference": "standard-ach"\n  }'`,
              node: `// 5201 cents → deterministic R01 return in test mode\nconst payment = await melio.payments.create(\n  "me",\n  {\n    amount: 5201,\n    originatingAccountId: "acct_3f2504e0-4f89-41d3-9a0c-0305e82c3301",\n    receivingAccountId: "acct_9b1deb4d-3b7d-4bad-9bdd-2b0d7b3dcb6d",\n    scheduledDate: "2026-06-10",\n    deliveryPreference: "standard-ach",\n  },\n  { idempotencyKey: "7b2e1a4c-9f3d-4e6a-8b1c-2d3e4f5a6b7c" }\n);`,
              python: `# 5201 cents → deterministic R01 return in test mode\npayment = melio.payments.create(\n    "me",\n    amount=5201,\n    originating_account_id="acct_3f2504e0-4f89-41d3-9a0c-0305e82c3301",\n    receiving_account_id="acct_9b1deb4d-3b7d-4bad-9bdd-2b0d7b3dcb6d",\n    scheduled_date="2026-06-10",\n    delivery_preference="standard-ach",\n    idempotency_key="7b2e1a4c-9f3d-4e6a-8b1c-2d3e4f5a6b7c",\n)`,
              go: `// 5201 cents → deterministic R01 return in test mode\npayment, err := client.Payments.Create(context.Background(), "me", &melio.PaymentParams{\n  Amount: melio.Int(5201),\n  IdempotencyKey: melio.String("7b2e1a4c-9f3d-4e6a-8b1c-2d3e4f5a6b7c"),\n})`,
            }}
            maxHeight={320}
          />
        </div>
      </TwoCol>
    );
  }
  if (id === 'errors') {
    return (
      <TwoCol>
        <div style={{ minWidth: 0, paddingBottom: 60 }}>
          <h1 className="ref-h1" style={{ margin: 0 }}>
            Errors
          </h1>
          <p className="ref-body-lg" style={{ marginTop: 14 }}>
            Errors use conventional HTTP status codes and always return the same shape: a stable <Code>code</Code>, a human{' '}
            <Code>message</Code>, and optional structured <Code>details</Code>. Branch on <Code>code</Code>, show <Code>message</Code>, act
            on <Code>details</Code>.
          </p>
          <H2>Status codes</H2>
          <ParamTable
            rows={[
              ['200 / 201', 'Success', 'Request succeeded. 201 on resource creation.'],
              ['204', 'Success, no content', 'Request succeeded — deletes and sandbox actions return no body.'],
              ['400', 'Bad request', 'Malformed JSON, a missing required field, or an attempt to change an immutable field.'],
              ['401', 'Unauthorized', 'Missing or invalid api-key.'],
              [
                '403',
                'Forbidden',
                'Not permitted — e.g. a scoped session token calling POST /entities/{entityId}/accounts for an external account.',
              ],
              ['404', 'Not found', 'No such resource, or me resolved to zero entities.'],
              [
                '409',
                'Conflict',
                'Entity not eligible, a duplicate externalId or entity, or a reused Idempotency-Key with a different body.',
              ],
              ['422', 'Unprocessable', 'Validation failed on a well-formed request.'],
              ['429', 'Rate limited', 'Too many requests. Back off and retry.'],
              ['503', 'Unavailable', 'Sandbox-only endpoints (e.g. Simulate a payment action) return this in production.'],
            ]}
          />
        </div>
        <div className="doc-aside" style={{ position: 'sticky', top: 88, minWidth: 0 }}>
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              color: 'var(--text-secondary)',
              marginBottom: 8,
              textTransform: 'uppercase',
              letterSpacing: '.04em',
            }}
          >
            The error shape
          </div>
          <CodeBlock dark={app.codeDark} title="409 Conflict" code={jsonStr(RES.paymentEligibilityError)} maxHeight={240} />
          <div style={{ marginTop: 14 }}>
            <CodeBlock
              dark={app.codeDark}
              title="Handling errors"
              langs={{
                node: `try {\n  await melio.payments.create("me", params);\n} catch (err) {\n  if (err.code === "BUSINESS_NOT_ELIGIBLE") {\n    // route the user back to KYC\n    console.log(err.details.missingFields);\n  }\n}`,
                python: `from melio import MelioError\n\ntry:\n    melio.payments.create("me", **params)\nexcept MelioError as err:\n    if err.code == "BUSINESS_NOT_ELIGIBLE":\n        print(err.details["missing_fields"])`,
                curl: `# Inspect the response body's error.code\n# and error.details to recover.`,
                go: `if err != nil {\n  var e *melio.Error\n  if errors.As(err, &e) && e.Code == "BUSINESS_NOT_ELIGIBLE" {\n    fmt.Println(e.Details.MissingFields)\n  }\n}`,
              }}
              maxHeight={240}
            />
          </div>
        </div>
      </TwoCol>
    );
  }
  if (id === 'idempotency') {
    return (
      <TwoCol>
        <div style={{ minWidth: 0, paddingBottom: 60 }}>
          <h1 className="ref-h1" style={{ margin: 0 }}>
            Idempotency
          </h1>
          <p className="ref-body-lg" style={{ marginTop: 14 }}>
            Network blips shouldn't pay a vendor twice. Send an <Code>Idempotency-Key</Code> request header (recommended: a UUID) on any
            create request and Melio guarantees the operation runs at most once. It is <strong>required when creating a payment</strong> —
            the rest of the create endpoints accept it but don't require it.
          </p>
          <H2>How it works</H2>
          <Prose>
            <ul style={{ margin: '8px 0', paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <li>The first request with a given key is processed and its result is stored.</li>
              <li>
                Any retry with the same key returns the <strong>original</strong> response — no second payment.
              </li>
              <li>Keys are scoped per partner and retained for 24 hours.</li>
              <li>
                Reusing a key with a <strong>different</strong> request body returns <Code>409 IDEMPOTENCY_KEY_REUSED</Code>.
              </li>
              <li>
                Creating a payment without the header returns <Code>400 IDEMPOTENCY_KEY_REQUIRED</Code>.
              </li>
            </ul>
          </Prose>
          <Banner kind="brand" style={{ marginTop: 16 }}>
            Use something stable from your own system — an order id, invoice number, or a UUID you persist before the call. The{' '}
            <Code>Idempotency-Key</Code> header is the only supported mechanism (there is no request-body idempotency field).
          </Banner>
        </div>
        <div className="doc-aside" style={{ position: 'sticky', top: 88, minWidth: 0 }}>
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              color: 'var(--text-secondary)',
              marginBottom: 8,
              textTransform: 'uppercase',
              letterSpacing: '.04em',
            }}
          >
            Safe retries
          </div>
          <CodeBlock
            dark={app.codeDark}
            langs={{
              curl: `curl https://api.melio.com/v2/entities/me/payments \\\n  -H "api-key: ${app.apiKey}" \\\n  -H "Idempotency-Key: 7b2e1a4c-9f3d-4e6a-8b1c-2d3e4f5a6b7c" \\\n  -X POST \\\n  -d '{\n    "amount": 125000\n  }'`,
              node: `const payment = await melio.payments.create(\n  "me",\n  {\n    amount: 125000,\n    originatingAccountId: "acct_3f2504e0-4f89-41d3-9a0c-0305e82c3301",\n    receivingAccountId: "acct_9b1deb4d-3b7d-4bad-9bdd-2b0d7b3dcb6d",\n    scheduledDate: "2026-06-10",\n  },\n  { idempotencyKey: "7b2e1a4c-9f3d-4e6a-8b1c-2d3e4f5a6b7c" }\n);`,
              python: `payment = melio.payments.create(\n    "me",\n    amount=125000,\n    originating_account_id="acct_3f2504e0-4f89-41d3-9a0c-0305e82c3301",\n    receiving_account_id="acct_9b1deb4d-3b7d-4bad-9bdd-2b0d7b3dcb6d",\n    scheduled_date="2026-06-10",\n    idempotency_key="7b2e1a4c-9f3d-4e6a-8b1c-2d3e4f5a6b7c",\n)`,
              go: `payment, err := client.Payments.Create(context.Background(), "me", &melio.PaymentParams{\n  Amount: melio.Int(125000),\n  IdempotencyKey: melio.String("7b2e1a4c-9f3d-4e6a-8b1c-2d3e4f5a6b7c"),\n})`,
            }}
            maxHeight={320}
          />
        </div>
      </TwoCol>
    );
  }
  if (id === 'pagination') {
    return (
      <TwoCol>
        <div style={{ minWidth: 0, paddingBottom: 60 }}>
          <h1 className="ref-h1" style={{ margin: 0 }}>
            Pagination
          </h1>
          <p className="ref-body-lg" style={{ marginTop: 14 }}>
            List payments is cursor-paginated and always returns results newest-first. Each response carries a <Code>data</Code> array and a{' '}
            <Code>hasMore</Code> flag; pass the last id as <Code>startingAfter</Code> to fetch the next page.
          </p>
          <H2>Parameters</H2>
          <ParamTable
            rows={[
              ['limit', 'integer', 'Page size, 1–100. Defaults to 25.'],
              ['startingAfter', 'string', 'An object id; returns the page after it. Mutually exclusive with endingBefore.'],
              ['endingBefore', 'string', 'An object id; returns the page before it. Mutually exclusive with startingAfter.'],
            ]}
          />
        </div>
        <div className="doc-aside" style={{ position: 'sticky', top: 88, minWidth: 0 }}>
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              color: 'var(--text-secondary)',
              marginBottom: 8,
              textTransform: 'uppercase',
              letterSpacing: '.04em',
            }}
          >
            Walk every page
          </div>
          <CodeBlock
            dark={app.codeDark}
            langs={{
              node: `let startingAfter;\ndo {\n  const page = await melio.payments.list("me", {\n    limit: 50,\n    startingAfter,\n  });\n  for (const p of page.data) handle(p);\n  startingAfter = page.hasMore\n    ? page.data.at(-1).id\n    : null;\n} while (startingAfter);`,
              python: `starting_after = None\nwhile True:\n    page = melio.payments.list(\n        "me", limit=50, starting_after=starting_after)\n    for p in page.data:\n        handle(p)\n    if not page.has_more:\n        break\n    starting_after = page.data[-1].id`,
              curl: `curl "https://api.melio.com/v2/entities/me/payments?limit=50&startingAfter=pay_9f8c7b6a5d4e3f21" \\\n  -H "api-key: ${app.apiKey}"`,
              go: `params := &melio.ListParams{Limit: melio.Int(50)}\niter := client.Payments.List(ctx, "me", params)\nfor iter.Next() {\n  handle(iter.Payment())\n}`,
            }}
            maxHeight={320}
          />
        </div>
      </TwoCol>
    );
  }
  return null;
}

// ---- Shell --------------------------------------------------------------
interface ApiReferenceProps {
  go: Go;
}

// The reference page is one continuous scrollable document: every guide page and endpoint is an
// anchored <section>, the sidebar highlights whichever one is in view (see useScrollSpy), and
// clicking a sidebar item smooth-scrolls to it rather than routing to a new page.
const HEADER_OFFSET = 88;

function ApiReference({ go }: ApiReferenceProps) {
  // `:section` is only used to resolve an initial deep-link scroll target on mount — once loaded,
  // navigation within the page is scroll-driven, not route-driven.
  const { section } = useParams();
  const suppressUntilRef = useRef(0);

  const sections = useMemo(() => buildSections(GROUPS as any), []);
  const order = useMemo(() => sections.map((s) => s.fullId), [sections]);
  const slugToFullId = useMemo(() => buildSlugToFullId(sections), [sections]);
  const { activeId, registerSection } = useScrollSpy(order, { headerOffset: HEADER_OFFSET });
  const selected = activeId ?? 'guide:intro';

  useEffect(() => {
    if (!section) return;
    const fullId = slugToFullId[section];
    if (!fullId) return;
    document.getElementById(fullId)?.scrollIntoView({ block: 'start' });
    // Initial-load deep link only — intentionally runs once, not on every `section`/`slugToFullId` change.
  }, []);

  useEffect(() => {
    if (!activeId || Date.now() < suppressUntilRef.current) return;
    const slug = activeId.slice(activeId.indexOf(':') + 1);
    window.history.replaceState(null, '', '/reference/' + slug);
  }, [activeId]);

  const onSelect = (fullId: string) => {
    suppressUntilRef.current = Date.now() + 700;
    document.getElementById(fullId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.replaceState(null, '', '/reference/' + fullId.slice(fullId.indexOf(':') + 1));
  };

  return (
    <div
      style={{ display: 'flex', width: '100%', background: 'var(--bg-primary)', minHeight: '100vh', fontFamily: 'var(--font-linear-sans)' }}
    >
      <RefNav selected={selected} onSelect={onSelect} />
      <div style={{ flex: 1, minWidth: 0, padding: '36px 48px' }}>
        <ApiOverview />
        {sections.map((s, i) => (
          <div key={s.fullId}>
            <section id={s.fullId} data-section-id={s.fullId} ref={registerSection(s.fullId)} style={{ scrollMarginTop: 96 }}>
              {s.kind === 'guide' ? <GuideDoc id={s.id} go={go} /> : <EndpointDoc ep={s.ep} />}
            </section>
            {i < sections.length - 1 && <hr style={{ border: 'none', borderTop: '1px solid var(--border-static)', margin: '96px 0' }} />}
          </div>
        ))}
      </div>
    </div>
  );
}

export { GuideDoc, ApiReference, Prose, H2, TwoCol };
